import axios from 'axios';

// Flag to prevent multiple refresh requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Setup axios interceptor for automatic token refresh
export const setupAxiosInterceptors = () => {
  // Request interceptor to add auth token
  axios.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle token refresh
  axios.interceptors.response.use(
    (response) => {
      // Check if server indicates token refresh is needed
      if (response.headers['x-token-refresh-needed'] === 'true') {
        console.log('🔄 Server indicates token refresh needed');
        refreshToken();
      }
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Check if error is due to expired token
      if (error.response?.status === 401 && 
          error.response?.data?.errorCode === 'TOKEN_EXPIRED' && 
          !originalRequest._retry) {
        
        if (isRefreshing) {
          // If already refreshing, queue the request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return axios(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const token = localStorage.getItem('token');
          if (!token) {
            throw new Error('No token available');
          }

          console.log('🔄 Attempting token refresh...');
          const response = await axios.post('/api/auth/refresh', {}, {
            headers: { Authorization: `Bearer ${token}` }
          });

          const newToken = response.data.token;
          localStorage.setItem('token', newToken);
          
          // Update default header
          axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          
          console.log('✅ Token refreshed successfully');
          
          // Process queued requests
          processQueue(null, newToken);
          
          // Retry original request
          originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
          return axios(originalRequest);
          
        } catch (refreshError) {
          console.error('❌ Token refresh failed:', refreshError);
          
          // Clear stored credentials
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          
          // Process queued requests with error
          processQueue(refreshError, null);
          
          // Redirect to login
          window.location.href = '/admin/login';
          
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Handle database connection errors with retry
      if (error.response?.status === 503 && error.response?.data?.errorCode === 'DB_UNAVAILABLE') {
        console.log('💾 Database connection issue, retrying in 3 seconds...');
        
        return new Promise((resolve, reject) => {
          setTimeout(() => {
            console.log('🔄 Retrying request after database reconnection...');
            axios(originalRequest)
              .then(resolve)
              .catch(reject);
          }, 3000);
        });
      }

      // Handle other auth errors
      if (error.response?.status === 401 && !error.response?.data?.preserveSession) {
        // Check if auth error and not explicitly preserving session
        console.log('🚺 Authentication expired, redirecting to login');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/admin/login';
      } else if (error.response?.status === 401 && error.response?.data?.preserveSession) {
        // Auth error but with preserve session flag
        console.log('⚠️ Auth error received but preserveSession flag set - not logging out');
      }

      // Log all errors for debugging
      if (error.response?.status >= 500) {
        console.error('🔥 Server error:', {
          status: error.response.status,
          message: error.response.data?.message,
          url: error.config?.url,
          method: error.config?.method
        });
      }

      return Promise.reject(error);
    }
  );
};

// Manual token refresh function
export const refreshToken = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No token available');
    }

    console.log('🔄 Manually refreshing token...');
    const response = await axios.post('/api/auth/refresh', {}, {
      headers: { Authorization: `Bearer ${token}` }
    });

    const newToken = response.data.token;
    localStorage.setItem('token', newToken);
    
    // Update default header
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    
    console.log('✅ Token manually refreshed');
    return newToken;
    
  } catch (error) {
    console.error('❌ Manual token refresh failed:', error);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/admin/login';
    throw error;
  }
};
